import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { recordDailyMatch, sanitizeDailyRecords, type DailyMatchAttempt, type DailyMatchRecords } from '../engine/precision/dailyMatch';
import type { TrainingPlanSnapshot, VisionAssessment } from '../data/visionAssessment';

export interface LevelResult {
  levelId: string;
  levelName: string;
  score: number;
  accuracy: number;
  reactionTime: number;
  completedAt: string;
  contrast: number;
  speed: number;
  mode?: 'standard' | 'challenge';
  challengeTier?: number;
  scoringVersion?: number;
  accuracyMeasured?: boolean;
  durationSeconds?: number;
  maxCombo?: number;
  mistakes?: number;
}

export interface Badge {
  id: string;
  name: string;
  icon: string;
  description: string;
  unlockedAt?: string;
}

export interface WeeklyData {
  date: string;
  accuracy: number;
  reactionTime: number;
  sessionsCompleted: number;
  totalTime: number;
}

export interface EyeProtectionDailyRecord {
  date: string;
  seconds: number;
}

export interface VisionQuestionnaireResult {
  answers: Record<string, number>;
  totalScore: number;
  maxScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  completedAt: string;
}

export interface AchievementRecord {
  id: string;
  unlockedAt: string;
  claimed: boolean;
  claimedAt?: string;
}

export interface CheckupRecord {
  id: string;
  date: string;
  hospital: string;
  doctor: string;
  result: 'normal' | 'attention' | 'treatment';
  leftEye: number;
  rightEye: number;
  notes: string;
}

export interface VisionProfile {
  date: string;
  age: number;
  gender: 'male' | 'female';
  leftEyeDiopter: number;
  rightEyeDiopter: number;
  leftEyeAstigmatism: number;
  rightEyeAstigmatism: number;
  parentMyopia: 'none' | 'one' | 'both';
  dailyScreenHours: number;
  dailyOutdoorHours: number;
  readingDistance: number; // cm
  sleepHours: number;
  lightingCondition: 'good' | 'average' | 'poor';
  note: string;
}

export interface AvatarUnlockCondition {
  id: string;
  type: 'free' | 'training_count' | 'streak' | 'energy' | 'accuracy' | 'reaction' | 'categories' | 'eye_protection';
  target: number;
  label: string;
}

/** 默认头像：简易 emoji 头像已下线，统一使用精选卡通头像 */
export const DEFAULT_AVATAR = '/avatars/archer-girl.png';

export const AVATAR_UNLOCK_CONDITIONS: AvatarUnlockCondition[] = [
  { id: 'archer-girl', type: 'free', target: 0, label: '初始头像' },
  { id: 'golden-warrior', type: 'free', target: 0, label: '初始头像' },
  { id: 'swordsman-blue', type: 'free', target: 0, label: '初始头像' },
  { id: 'monk-orange', type: 'free', target: 0, label: '初始头像' },
  { id: 'panda-chef', type: 'free', target: 0, label: '初始头像' },
  { id: 'lightning-kid', type: 'free', target: 0, label: '初始头像' },
  { id: 'snow-queen', type: 'free', target: 0, label: '初始头像' },
  { id: 'star-captain', type: 'free', target: 0, label: '初始头像' },
  { id: 'scholar-sword', type: 'training_count', target: 5, label: '完成5次训练' },
  { id: 'forest-archer', type: 'streak', target: 3, label: '连续训练3天' },
  { id: 'winged-general', type: 'energy', target: 50, label: '累计获得50能量' },
  { id: 'zen-monk', type: 'eye_protection', target: 1800, label: '护眼模式30分钟' },
  { id: 'wizard-boy', type: 'training_count', target: 10, label: '完成10次训练' },
  { id: 'sunflower-fairy', type: 'streak', target: 7, label: '连续训练7天' },
  { id: 'firefighter-kid', type: 'accuracy', target: 80, label: '准确率达80%' },
  { id: 'dj-cool', type: 'energy', target: 100, label: '累计获得100能量' },
  { id: 'dino-boy', type: 'training_count', target: 20, label: '完成20次训练' },
  { id: 'cat-girl', type: 'categories', target: 3, label: '完成3类游戏' },
  { id: 'robot-kid', type: 'accuracy', target: 90, label: '准确率达90%' },
  { id: 'mermaid-girl', type: 'streak', target: 14, label: '连续训练14天' },
  { id: 'pirate-captain', type: 'training_count', target: 50, label: '完成50次训练' },
  { id: 'fairy-princess', type: 'energy', target: 500, label: '累计获得500能量' },
  { id: 'ninja-kid', type: 'reaction', target: 500, label: '反应≤500ms' },
  { id: 'astronaut-boy', type: 'categories', target: 6, label: '完成全部6类游戏' },
  { id: 'star-captain', type: 'training_count', target: 30, label: '完成30次训练' },
  { id: 'iron-knight', type: 'energy', target: 200, label: '累计获得200能量' },
  { id: 'lightning-kid', type: 'reaction', target: 400, label: '反应≤400ms' },
  { id: 'fox-fairy', type: 'streak', target: 21, label: '连续训练21天' },
  { id: 'sports-star', type: 'eye_protection', target: 3600, label: '护眼模式60分钟' },
  { id: 'snow-queen', type: 'accuracy', target: 95, label: '准确率达95%' },
  { id: 'rainbow-painter', type: 'eye_protection', target: 7200, label: '护眼模式120分钟' },
  { id: 'panda-chef', type: 'streak', target: 30, label: '连续训练30天' },
  { id: 'dragon-rider', type: 'energy', target: 1000, label: '累计获得1000能量' },
  { id: 'moon-fairy', type: 'reaction', target: 350, label: '反应≤350ms' },
  { id: 'rock-star', type: 'training_count', target: 100, label: '完成100次训练' },
  { id: 'little-witch', type: 'training_count', target: 200, label: '完成200次训练' },
];

export interface GameState {
  // Player info
  playerName: string;
  playerAvatar: string;
  energy: number;
  totalEnergy: number;
  consecutiveDays: number;
  lastPlayDate: string;
  
  // Progress
  currentChapter: number;
  unlockedLevels: string[];
  completedLevels: string[];
  unlockedScenes: string[];
  
  // Difficulty adaptation
  contrastLevel: number;
  speedLevel: number;
  
  // Results
  levelResults: LevelResult[];
  dailyMatchRecords: DailyMatchRecords;
  settleDailyMatch: (attempt: DailyMatchAttempt) => ReturnType<typeof recordDailyMatch>;
  weeklyData: WeeklyData[];
  
  // Achievements
  badges: Badge[];
  
  // Shop
  cursorSkins: string[];
  activeCursor: string;
  
  // Theme
  darkMode: boolean;
  eyeProtection: boolean;
  colorTemperature: number; // 0-100, higher = warmer
  eyeReminderEnabled: boolean;
  eyeReminderInterval: number; // minutes
  
  // Auto night eye protection
  autoNightEyeProtection: boolean;
  nightStartHour: number; // 0-23
  nightEndHour: number; // 0-23
  
  // Eye protection usage stats
  eyeProtectionStartTime: number | null; // timestamp when protection was turned on
  eyeProtectionTotalSeconds: number; // all-time cumulative seconds
  eyeProtectionTodaySeconds: number; // today's cumulative seconds
  eyeProtectionLastDate: string; // date string to track "today"
  eyeProtectionWeekly: EyeProtectionDailyRecord[]; // last 7 days
  
  // Vision questionnaire
  visionQuestionnaireResults: VisionQuestionnaireResult[];
  
  // 视功能评估（智能推荐模式：问卷 + 检查结果）
  visionAssessments: VisionAssessment[];
  /** 最近一次评估生成的训练计划，供首页直接开练 */
  activeTrainingPlan: TrainingPlanSnapshot | null;
  
  // Vision records (diopter tracking)
  visionRecords: { date: string; leftEye: number; rightEye: number; note: string }[];
  
  // Achievements
  achievements: AchievementRecord[];
  
  // Unlocked avatars
  unlockedAvatars: string[];
  
  // Daily check-in
  checkinDates: string[];
  checkinStreak: number;
  lastCheckinDate: string;
  
  // Vision profile (myopia risk tracking)
  visionProfiles: VisionProfile[];
  
  // Follow-up reminder
  followUpReminderEnabled: boolean;
  followUpIntervalDays: number; // 30, 60, 90, 180
  lastCheckupDate: string; // ISO date string
  followUpDismissedDate: string; // date when user dismissed the reminder
  checkupHistory: CheckupRecord[];
  
  // Actions
  setPlayerName: (name: string) => void;
  setPlayerAvatar: (avatar: string) => void;
  toggleDarkMode: () => void;
  toggleEyeProtection: () => void;
  setColorTemperature: (value: number) => void;
  toggleEyeReminder: () => void;
  setEyeReminderInterval: (minutes: number) => void;
  startEyeProtectionTracking: () => void;
  stopEyeProtectionTracking: () => void;
  toggleAutoNightEyeProtection: () => void;
  setNightStartHour: (hour: number) => void;
  setNightEndHour: (hour: number) => void;
  saveVisionQuestionnaire: (result: VisionQuestionnaireResult) => void;
  saveVisionAssessment: (assessment: VisionAssessment, plan: TrainingPlanSnapshot) => void;
  setActiveTrainingPlan: (plan: TrainingPlanSnapshot | null) => void;
  clearVisionAssessments: () => void;
  addVisionRecord: (record: { date: string; leftEye: number; rightEye: number; note: string }) => void;
  saveVisionProfile: (profile: VisionProfile) => void;
  deleteVisionProfile: (date: string) => void;
  setFollowUpReminderEnabled: (enabled: boolean) => void;
  setFollowUpIntervalDays: (days: number) => void;
  setLastCheckupDate: (date: string) => void;
  dismissFollowUpReminder: () => void;
  addCheckupRecord: (record: CheckupRecord) => void;
  deleteCheckupRecord: (id: string) => void;
  claimAchievementReward: (achievementId: string, reward: number) => void;
  unlockAvatar: (avatarId: string) => void;
  checkAvatarUnlocks: () => string[]; // returns newly unlocked avatar IDs
  dailyCheckin: () => number; // returns energy reward
  addEnergy: (amount: number) => void;
  spendEnergy: (amount: number) => boolean;
  completeLevel: (result: LevelResult) => void;
  unlockLevel: (levelId: string) => void;
  unlockScene: (scene: string) => void;
  unlockBadge: (badge: Badge) => void;
  buyCursorSkin: (skinId: string) => void;
  setActiveCursor: (skinId: string) => void;
  adjustDifficulty: (accuracy: number, reactionTime: number) => void;
  updateConsecutiveDays: () => void;
  resetGame: () => void;
}

const defaultBadges: Badge[] = [
  { id: 'first_clear', name: '初次通关', icon: '🌟', description: '完成第一个关卡' },
  { id: 'sharp_eye', name: '火眼金睛', icon: '👁️', description: '准确率达到95%以上' },
  { id: 'speed_demon', name: '闪电反应', icon: '⚡', description: '反应时间低于500ms' },
  { id: 'streak_3', name: '三日坚持', icon: '🔥', description: '连续训练3天' },
  { id: 'streak_7', name: '周冠达人', icon: '👑', description: '连续训练7天' },
  { id: 'energy_100', name: '能量满溢', icon: '💎', description: '累计获得100能量' },
  { id: 'explorer', name: '场景探险家', icon: '🗺️', description: '解锁所有场景' },
  { id: 'memory_master', name: '记忆大师', icon: '🧠', description: '记忆拼图关卡全部完成' },
];

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      playerName: '',
      playerAvatar: '🧒',
      energy: 0,
      totalEnergy: 0,
      consecutiveDays: 0,
      lastPlayDate: '',
      
      currentChapter: 1,
      unlockedLevels: ['flip-tracking', 'color-identify'],
      completedLevels: [],
      unlockedScenes: ['forest'],
      
      contrastLevel: 0.7,
      speedLevel: 1.0,
      
      levelResults: [],
      dailyMatchRecords: {},
      weeklyData: [],
      
      badges: [],
      
      cursorSkins: ['default'],
      activeCursor: 'default',
      
      darkMode: false,
      eyeProtection: false,
      colorTemperature: 40,
      eyeReminderEnabled: true,
      eyeReminderInterval: 20,
      
      autoNightEyeProtection: false,
      nightStartHour: 21,
      nightEndHour: 7,
      
      eyeProtectionStartTime: null,
      eyeProtectionTotalSeconds: 0,
      eyeProtectionTodaySeconds: 0,
      eyeProtectionLastDate: '',
      eyeProtectionWeekly: [],
      
      visionQuestionnaireResults: [],
      
      visionAssessments: [],
      activeTrainingPlan: null,
      
      visionRecords: [],
      
      achievements: [],
      
      unlockedAvatars: ['archer-girl', 'golden-warrior', 'swordsman-blue', 'monk-orange', 'panda-chef', 'lightning-kid', 'snow-queen', 'star-captain'],
      
      checkinDates: [],
      checkinStreak: 0,
      lastCheckinDate: '',
      
      visionProfiles: [],
      
      followUpReminderEnabled: true,
      followUpIntervalDays: 90,
      lastCheckupDate: '',
      followUpDismissedDate: '',
      checkupHistory: [],
      
      saveVisionQuestionnaire: (result) => {
        const results = [...get().visionQuestionnaireResults, result].slice(-10);
        set({ visionQuestionnaireResults: results });
      },
      
      saveVisionAssessment: (assessment, plan) => {
        const assessments = [...get().visionAssessments, assessment].slice(-10);
        set({ visionAssessments: assessments, activeTrainingPlan: plan });
      },
      
      setActiveTrainingPlan: (plan) => set({ activeTrainingPlan: plan }),
      
      clearVisionAssessments: () => set({ visionAssessments: [], activeTrainingPlan: null }),
      
      addVisionRecord: (record) => {
        const records = [...get().visionRecords, record].slice(-20);
        set({ visionRecords: records });
      },
      
      saveVisionProfile: (profile) => {
        const profiles = [...get().visionProfiles, profile].slice(-30);
        set({ visionProfiles: profiles });
      },
      
      deleteVisionProfile: (date) => {
        const profiles = get().visionProfiles.filter(p => p.date !== date);
        set({ visionProfiles: profiles });
      },
      
      setFollowUpReminderEnabled: (enabled) => set({ followUpReminderEnabled: enabled }),
      
      setFollowUpIntervalDays: (days) => set({ followUpIntervalDays: days }),
      
      setLastCheckupDate: (date) => set({ lastCheckupDate: date, followUpDismissedDate: '' }),
      
      dismissFollowUpReminder: () => {
        const today = new Date().toISOString().split('T')[0];
        set({ followUpDismissedDate: today });
      },
      
      addCheckupRecord: (record) => {
        const history = [...get().checkupHistory, record].slice(-30);
        // Also update lastCheckupDate to the most recent record
        const sortedDates = history.map(r => r.date).sort();
        const latestDate = sortedDates[sortedDates.length - 1];
        set({ checkupHistory: history, lastCheckupDate: latestDate, followUpDismissedDate: '' });
      },
      
      deleteCheckupRecord: (id) => {
        const history = get().checkupHistory.filter(r => r.id !== id);
        const sortedDates = history.map(r => r.date).sort();
        const latestDate = sortedDates.length > 0 ? sortedDates[sortedDates.length - 1] : '';
        set({ checkupHistory: history, lastCheckupDate: latestDate });
      },
      
      dailyCheckin: () => {
        const state = get();
        const today = new Date().toISOString().split('T')[0];
        if (state.lastCheckinDate === today) return 0; // Already checked in
        
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
        let newStreak = 1;
        if (state.lastCheckinDate === yesterday) {
          newStreak = state.checkinStreak + 1;
        }
        // Cap streak display at 7 for reward calculation, but keep counting
        const rewardDay = ((newStreak - 1) % 7) + 1;
        const rewards = [5, 8, 12, 15, 20, 25, 50];
        const reward = rewards[rewardDay - 1] || 5;
        
        const newDates = [...state.checkinDates, today].slice(-30); // Keep 30 days
        set({
          checkinDates: newDates,
          checkinStreak: newStreak,
          lastCheckinDate: today,
          energy: state.energy + reward,
          totalEnergy: state.totalEnergy + reward,
        });
        return reward;
      },
      
      claimAchievementReward: (achievementId, reward) => {
        const state = get();
        const existing = state.achievements.find(a => a.id === achievementId);
        if (existing?.claimed) return;
        
        const now = new Date().toISOString();
        let newAchievements: AchievementRecord[];
        if (existing) {
          newAchievements = state.achievements.map(a => 
            a.id === achievementId ? { ...a, claimed: true, claimedAt: now } : a
          );
        } else {
          newAchievements = [...state.achievements, { id: achievementId, unlockedAt: now, claimed: true, claimedAt: now }];
        }
        
        set({ 
          achievements: newAchievements,
          energy: state.energy + reward,
          totalEnergy: state.totalEnergy + reward,
        });
      },
      
      unlockAvatar: (avatarId) => {
        const state = get();
        if (!state.unlockedAvatars.includes(avatarId)) {
          set({ unlockedAvatars: [...state.unlockedAvatars, avatarId] });
        }
      },
      
      checkAvatarUnlocks: () => {
        const state = get();
        const newlyUnlocked: string[] = [];
        const trainingCount = state.levelResults.length;
        const streak = state.consecutiveDays;
        const energy = state.totalEnergy;
        const eyeSeconds = state.eyeProtectionTotalSeconds;
        const bestAccuracy = state.levelResults.length > 0 
          ? Math.max(...state.levelResults.map(r => r.accuracy)) 
          : 0;
        const measuredReactions = state.levelResults.filter(r => r.reactionTime > 0);
        const bestReaction = measuredReactions.length > 0
          ? Math.min(...measuredReactions.map(r => r.reactionTime))
          : Infinity;
        // Count unique game categories completed
        const completedGameIds = new Set(state.completedLevels);
        const categoryCount = new Set(
          state.levelResults.map(r => r.levelId.split('-')[0])
        ).size;
        
        for (const condition of AVATAR_UNLOCK_CONDITIONS) {
          if (state.unlockedAvatars.includes(condition.id)) continue;
          if (condition.type === 'free') continue; // already handled at init
          
          let unlocked = false;
          switch (condition.type) {
            case 'training_count':
              unlocked = trainingCount >= condition.target;
              break;
            case 'streak':
              unlocked = streak >= condition.target;
              break;
            case 'energy':
              unlocked = energy >= condition.target;
              break;
            case 'eye_protection':
              unlocked = eyeSeconds >= condition.target;
              break;
            case 'accuracy':
              unlocked = bestAccuracy >= condition.target;
              break;
            case 'reaction':
              unlocked = bestReaction <= condition.target;
              break;
            case 'categories':
              unlocked = categoryCount >= condition.target;
              break;
          }
          
          if (unlocked) {
            newlyUnlocked.push(condition.id);
          }
        }
        
        if (newlyUnlocked.length > 0) {
          set({ unlockedAvatars: [...state.unlockedAvatars, ...newlyUnlocked] });
        }
        return newlyUnlocked;
      },
      
      setPlayerName: (name) => set({ playerName: name }),
      setPlayerAvatar: (avatar) => set({ playerAvatar: avatar }),
      
      toggleDarkMode: () => {
        const newMode = !get().darkMode;
        set({ darkMode: newMode });
        if (newMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },
      
      toggleEyeProtection: () => {
        const wasOn = get().eyeProtection;
        if (wasOn) {
          // Turning off - stop tracking
          get().stopEyeProtectionTracking();
        }
        set({ eyeProtection: !wasOn });
        if (!wasOn) {
          // Turning on - start tracking
          get().startEyeProtectionTracking();
        }
      },
      
      setColorTemperature: (value) => set({ colorTemperature: Math.max(0, Math.min(100, value)) }),
      
      startEyeProtectionTracking: () => {
        const today = new Date().toISOString().split('T')[0];
        const state = get();
        // Reset daily counter if date changed
        if (state.eyeProtectionLastDate !== today) {
          set({
            eyeProtectionTodaySeconds: 0,
            eyeProtectionLastDate: today,
          });
        }
        set({ eyeProtectionStartTime: Date.now() });
      },
      
      stopEyeProtectionTracking: () => {
        const state = get();
        if (!state.eyeProtectionStartTime) return;
        const elapsed = Math.floor((Date.now() - state.eyeProtectionStartTime) / 1000);
        if (elapsed <= 0) {
          set({ eyeProtectionStartTime: null });
          return;
        }
        const today = new Date().toISOString().split('T')[0];
        const newTotal = state.eyeProtectionTotalSeconds + elapsed;
        let newToday = state.eyeProtectionTodaySeconds + elapsed;
        // Reset if day changed since last tracking
        if (state.eyeProtectionLastDate !== today) {
          newToday = elapsed;
        }
        // Update weekly records
        const weekly = [...state.eyeProtectionWeekly];
        const todayRecord = weekly.find(r => r.date === today);
        if (todayRecord) {
          todayRecord.seconds += elapsed;
        } else {
          weekly.push({ date: today, seconds: elapsed });
        }
        // Keep only last 7 days
        const recentWeekly = weekly.slice(-7);
        set({
          eyeProtectionStartTime: null,
          eyeProtectionTotalSeconds: newTotal,
          eyeProtectionTodaySeconds: newToday,
          eyeProtectionLastDate: today,
          eyeProtectionWeekly: recentWeekly,
        });
      },
      
      toggleEyeReminder: () => set({ eyeReminderEnabled: !get().eyeReminderEnabled }),
      
      setEyeReminderInterval: (minutes) => set({ eyeReminderInterval: Math.max(5, Math.min(60, minutes)) }),
      
      toggleAutoNightEyeProtection: () => set({ autoNightEyeProtection: !get().autoNightEyeProtection }),
      
      setNightStartHour: (hour) => set({ nightStartHour: Math.max(0, Math.min(23, hour)) }),
      
      setNightEndHour: (hour) => set({ nightEndHour: Math.max(0, Math.min(23, hour)) }),
      
      addEnergy: (amount) => {
        const state = get();
        const newEnergy = state.energy + amount;
        const newTotal = state.totalEnergy + amount;
        set({ energy: newEnergy, totalEnergy: newTotal });
        if (newTotal >= 100 && !state.badges.find(b => b.id === 'energy_100')) {
          get().unlockBadge(defaultBadges.find(b => b.id === 'energy_100')!);
        }
      },
      
      spendEnergy: (amount) => {
        const state = get();
        if (state.energy >= amount) {
          set({ energy: state.energy - amount });
          return true;
        }
        return false;
      },
      
      settleDailyMatch: (attempt) => {
        const state = get();
        const outcome = recordDailyMatch(state.dailyMatchRecords, attempt);
        if (!outcome.recorded) return outcome;
        // Persist the reward and its claim marker together; retries never reward twice.
        set({ dailyMatchRecords: outcome.records, energy: state.energy + outcome.reward, totalEnergy: state.totalEnergy + outcome.reward });
        if (outcome.reward > 0) {
          if (get().totalEnergy >= 100 && !get().badges.some(b => b.id === 'energy_100')) get().unlockBadge(defaultBadges.find(b => b.id === 'energy_100')!);
          get().checkAvatarUnlocks();
        }
        return outcome;
      },

      completeLevel: (result) => {
        const state = get();
        const newResults = [...state.levelResults, result];
        const completed = state.completedLevels.includes(result.levelId) 
          ? state.completedLevels 
          : [...state.completedLevels, result.levelId];
        
        set({ levelResults: newResults, completedLevels: completed });
        
        // Check badges
        if (completed.length === 1 && !state.badges.find(b => b.id === 'first_clear')) {
          get().unlockBadge(defaultBadges.find(b => b.id === 'first_clear')!);
        }
        if (result.accuracy >= 95 && !state.badges.find(b => b.id === 'sharp_eye')) {
          get().unlockBadge(defaultBadges.find(b => b.id === 'sharp_eye')!);
        }
        if (result.reactionTime > 0 && result.reactionTime <= 500 && !state.badges.find(b => b.id === 'speed_demon')) {
          get().unlockBadge(defaultBadges.find(b => b.id === 'speed_demon')!);
        }
        
        // Auto adjust difficulty
        if (result.accuracyMeasured !== false && result.reactionTime > 0) get().adjustDifficulty(result.accuracy, result.reactionTime);
        
        // Check avatar unlocks after completing a level
        get().checkAvatarUnlocks();
      },
      
      unlockLevel: (levelId) => {
        const state = get();
        if (!state.unlockedLevels.includes(levelId)) {
          set({ unlockedLevels: [...state.unlockedLevels, levelId] });
        }
      },
      
      unlockScene: (scene) => {
        const state = get();
        if (!state.unlockedScenes.includes(scene)) {
          set({ unlockedScenes: [...state.unlockedScenes, scene] });
          if (state.unlockedScenes.length + 1 >= 3) {
            get().unlockBadge(defaultBadges.find(b => b.id === 'explorer')!);
          }
        }
      },
      
      unlockBadge: (badge) => {
        const state = get();
        if (!state.badges.find(b => b.id === badge.id)) {
          set({ badges: [...state.badges, { ...badge, unlockedAt: new Date().toISOString() }] });
        }
      },
      
      buyCursorSkin: (skinId) => {
        const state = get();
        if (!state.cursorSkins.includes(skinId)) {
          set({ cursorSkins: [...state.cursorSkins, skinId] });
        }
      },
      
      setActiveCursor: (skinId) => set({ activeCursor: skinId }),
      
      adjustDifficulty: (accuracy, reactionTime) => {
        const state = get();
        let newContrast = state.contrastLevel;
        let newSpeed = state.speedLevel;
        
        if (accuracy > 90 && reactionTime < 600) {
          // Player doing well - increase difficulty
          newContrast = Math.max(0.3, newContrast - 0.05);
          newSpeed = Math.min(2.0, newSpeed + 0.1);
        } else if (accuracy < 60 || reactionTime > 1500) {
          // Player struggling - decrease difficulty
          newContrast = Math.min(1.0, newContrast + 0.05);
          newSpeed = Math.max(0.5, newSpeed - 0.1);
        }
        
        set({ contrastLevel: newContrast, speedLevel: newSpeed });
      },
      
      updateConsecutiveDays: () => {
        const state = get();
        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
        
        if (state.lastPlayDate === today) return;
        
        let newStreak = 1;
        if (state.lastPlayDate === yesterday) {
          newStreak = state.consecutiveDays + 1;
        }
        
        set({ consecutiveDays: newStreak, lastPlayDate: today });
        
        if (newStreak >= 3 && !state.badges.find(b => b.id === 'streak_3')) {
          get().unlockBadge(defaultBadges.find(b => b.id === 'streak_3')!);
        }
        if (newStreak >= 7 && !state.badges.find(b => b.id === 'streak_7')) {
          get().unlockBadge(defaultBadges.find(b => b.id === 'streak_7')!);
        }
        
        // Unlock scenes based on consecutive days
        if (newStreak >= 5) get().unlockScene('ocean');
        if (newStreak >= 10) get().unlockScene('space');
        
        // Check avatar unlocks after streak update
        get().checkAvatarUnlocks();
      },
      
      resetGame: () => set({
        energy: 0,
        totalEnergy: 0,
        consecutiveDays: 0,
        lastPlayDate: '',
        currentChapter: 1,
        unlockedLevels: ['flip-tracking', 'color-identify'],
        completedLevels: [],
        unlockedScenes: ['forest'],
        contrastLevel: 0.7,
        speedLevel: 1.0,
        levelResults: [],
        dailyMatchRecords: {},
        weeklyData: [],
        badges: [],
        cursorSkins: ['default'],
        activeCursor: 'default',
        visionAssessments: [],
        activeTrainingPlan: null,
        unlockedAvatars: AVATAR_UNLOCK_CONDITIONS.filter(c => c.type === 'free').map(c => c.id),
        playerAvatar: DEFAULT_AVATAR,
      }),
    }),
    {
      name: 'vision-quest-game',
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<GameState>;
        const rawResults: unknown[] = Array.isArray(saved.levelResults) ? saved.levelResults : [];
        const recoveredIds = rawResults.filter((r): r is string => typeof r === 'string');
        const validResults = rawResults.filter((r): r is LevelResult => Boolean(r) && typeof r === 'object' && typeof (r as LevelResult).levelId === 'string' && Number.isFinite((r as LevelResult).score));
        // 免费头像扩充后必须补给老用户：初始解锁列表与持久化值取并集，
        // 否则旧 localStorage（4 个免费）会盖掉新增免费头像
        const FREE_AVATARS = AVATAR_UNLOCK_CONDITIONS.filter(c => c.type === 'free').map(c => c.id);
        const mergedUnlocked = [...new Set([...(Array.isArray(saved.unlockedAvatars) ? saved.unlockedAvatars : []), ...FREE_AVATARS])];
        // 头像设置已移除 emoji 简易头像：存量 emoji 头像回落到默认卡通头像，
        // 否则头像位会渲染成空白
        const savedAvatar = typeof saved.playerAvatar === 'string' ? saved.playerAvatar : '';
        const isEmojiAvatar = Boolean(savedAvatar)
          && !savedAvatar.startsWith('data:image')
          && !savedAvatar.startsWith('http')
          && !savedAvatar.startsWith('/avatars/');
        const playerAvatar = isEmojiAvatar ? DEFAULT_AVATAR : (savedAvatar || current.playerAvatar);
        return { ...current, ...saved, playerAvatar, unlockedAvatars: mergedUnlocked, levelResults: validResults, dailyMatchRecords: sanitizeDailyRecords(saved.dailyMatchRecords),
          completedLevels: [...new Set([...(saved.completedLevels ?? []).filter(id => typeof id === 'string'), ...recoveredIds])],
        };
      },
    }
  )
);
